import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import type { SlotColourRow } from '@/lib/vehicleTypes';
import { Plus, Trash2 } from 'lucide-react';

interface VehicleSlotColourSettingsProps {
  vehicleTypes: { vehicle_type: string; tenant_id: string }[];
}

const DEFAULT_PALETTE = [
  { booked: '#BFDBFE', available: '#1D4ED8' },
  { booked: '#BBF7D0', available: '#15803D' },
  { booked: '#FDE68A', available: '#B45309' },
  { booked: '#DDD6FE', available: '#6D28D9' },
  { booked: '#FBCFE8', available: '#BE185D' },
  { booked: '#A5F3FC', available: '#0E7490' },
];

const VehicleSlotColourSettings: React.FC<VehicleSlotColourSettingsProps> = ({ vehicleTypes }) => {
  const [colours, setColours] = useState<SlotColourRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  useEffect(() => {
    loadColours();
  }, []);

  const loadColours = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('vehicle_type_slot_colours')
      .select('vehicle_type, slot_number, booked_colour_hex, available_colour_hex')
      .order('slot_number', { ascending: true });
    if (error) {
      console.error('Error loading slot colours:', error);
      toast({ title: 'Error', description: 'Failed to load slot colours', variant: 'destructive' });
    } else {
      setColours((data as SlotColourRow[]) || []);
    }
    setLoading(false);
  };

  const updateColour = async (
    vehicleType: string,
    slotNumber: number,
    field: 'booked_colour_hex' | 'available_colour_hex',
    value: string
  ) => {
    const key = `${vehicleType}-${slotNumber}-${field}`;
    setSavingKey(key);
    setColours((prev) =>
      prev.map((c) => (c.vehicle_type === vehicleType && c.slot_number === slotNumber ? { ...c, [field]: value } : c))
    );
    const { error } = await supabase
      .from('vehicle_type_slot_colours')
      .update({ [field]: value, updated_at: new Date().toISOString() })
      .eq('vehicle_type', vehicleType)
      .eq('slot_number', slotNumber);
    if (error) {
      toast({ title: 'Error', description: 'Failed to save colour: ' + error.message, variant: 'destructive' });
      loadColours();
    }
    setSavingKey(null);
  };

  const addSlot = async (vehicleType: string) => {
    const tenantRow = vehicleTypes.find((t) => t.vehicle_type === vehicleType);
    if (!tenantRow) return;
    const existing = colours.filter((c) => c.vehicle_type === vehicleType);
    const nextSlot = existing.length > 0 ? Math.max(...existing.map((c) => c.slot_number)) + 1 : 1;
    const palette = DEFAULT_PALETTE[(nextSlot - 1) % DEFAULT_PALETTE.length];
    const { error } = await supabase.from('vehicle_type_slot_colours').insert({
      tenant_id: tenantRow.tenant_id,
      vehicle_type: vehicleType,
      slot_number: nextSlot,
      booked_colour_hex: palette.booked,
      available_colour_hex: palette.available,
    });
    if (error) {
      toast({ title: 'Error', description: 'Failed to add slot: ' + error.message, variant: 'destructive' });
    } else {
      loadColours();
    }
  };

  const deleteSlot = async (vehicleType: string, slotNumber: number) => {
    const { error } = await supabase
      .from('vehicle_type_slot_colours')
      .delete()
      .eq('vehicle_type', vehicleType)
      .eq('slot_number', slotNumber);
    if (error) {
      toast({ title: 'Error', description: 'Failed to delete slot: ' + error.message, variant: 'destructive' });
    } else {
      setColours((prev) => prev.filter((c) => !(c.vehicle_type === vehicleType && c.slot_number === slotNumber)));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500" />
      </div>
    );
  }

  return (
    <Card className="p-4 border-gray-700 bg-gray-800/30">
      <h3 className="text-lg font-bold text-yellow-500">Slot Colours</h3>
      <p className="text-sm text-gray-400 mt-1 mb-4">
        The colour each numbered slot (Sedan 1, Sedan 2, TMV 1...) shows on the Fleet Capacity sheet — one shade for
        booked, a darker one for available. Add a slot here whenever your fleet needs another concurrent slot for that
        type.
      </p>

      <div className="space-y-5">
        {vehicleTypes.map((t) => {
          const slots = colours.filter((c) => c.vehicle_type === t.vehicle_type).sort((a, b) => a.slot_number - b.slot_number);
          const maxSlot = slots.length > 0 ? Math.max(...slots.map((s) => s.slot_number)) : 0;
          return (
            <div key={t.vehicle_type}>
              <p className="text-sm font-semibold text-gray-200 mb-2">{t.vehicle_type}</p>
              <div className="space-y-2">
                {slots.map((s) => (
                  <div key={s.slot_number} className="flex items-center gap-4 flex-wrap">
                    <span className="text-xs text-gray-400 w-16">
                      {t.vehicle_type} {s.slot_number}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <Label className="text-[10px] text-gray-500">Booked</Label>
                      <input
                        type="color"
                        value={s.booked_colour_hex}
                        onChange={(e) => updateColour(t.vehicle_type, s.slot_number, 'booked_colour_hex', e.target.value)}
                        disabled={savingKey === `${t.vehicle_type}-${s.slot_number}-booked_colour_hex`}
                        className="w-8 h-7 rounded cursor-pointer border border-gray-600 bg-transparent"
                      />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Label className="text-[10px] text-gray-500">Available</Label>
                      <input
                        type="color"
                        value={s.available_colour_hex}
                        onChange={(e) => updateColour(t.vehicle_type, s.slot_number, 'available_colour_hex', e.target.value)}
                        disabled={savingKey === `${t.vehicle_type}-${s.slot_number}-available_colour_hex`}
                        className="w-8 h-7 rounded cursor-pointer border border-gray-600 bg-transparent"
                      />
                    </div>
                    {s.slot_number === maxSlot && (
                      <button
                        onClick={() => deleteSlot(t.vehicle_type, s.slot_number)}
                        className="p-1 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition-colors"
                        title="Remove this slot"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => addSlot(t.vehicle_type)}>
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Add Slot
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default VehicleSlotColourSettings;
